import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/app/_core/_services/authentication.service';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';
import { UserService } from '../../_services/user.service';
import { MenuItem } from 'primeng/api';
import { map, Observable } from 'rxjs';
import { TenantConfigService } from '../../_services/tenant-config.service';
import { AREAS } from 'src/app/_core/_constants/area-permissions';

interface MenuItemWithPermission extends MenuItem {
  requiredPermission?: string | null;
}

interface User {
  id?: number;
  first_name?: string;
  last_name?: string;
  role?: {
    name?: string;
    description?: string;
  };
  tenant_id?: number;
  tenants?: Array<{
    id: number;
    name: string;
  }>;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @Input() menuItems: MenuItemWithPermission[] = [];
  @Input() rootPath: string = '';
  @Input() isCollapsed: boolean = false;
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() closeSidebar = new EventEmitter<void>();

  menuItems$: Observable<MenuItem[]>;
  tenantOptions: MenuItem[] = [];
  isMobile: boolean = false;
  userMenuItems: MenuItem[] = [];

  constructor(
    public authenticationService: AuthenticationService,
    public userService: UserService,
    public authz: AuthorizationService,
    public tcs: TenantConfigService,
    public router: Router
  ) {
    this.menuItems$ = this.userService.currentUser$.pipe(
      map((currentUser: User) => {
        return this.convertTenantsToMenuItems(currentUser?.tenants || []);
      })
    );
  }

  ngOnInit(): void {
    this.checkScreenSize();
    window.addEventListener('resize', () => this.checkScreenSize());

    this.userService.getCurrentLoggedInUser();

    // Initialize user menu items
    this.userMenuItems = [
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        routerLink: '/settings',
        command: () => {
          this.router.navigate(['/settings']);
        }
      },
      {
        label: 'Sign Out',
        icon: 'pi pi-sign-out',
        command: () => {
          this.authenticationService.sessionLogout();
          this.userService.clearCurrentLoggedInUser();
        }
      }
    ];

    // Menu visibility mirrors AREAS (area-permissions.ts) exactly, the same
    // source of truth the route guards use - keeps "what's in the
    // sidebar" and "what the guard actually lets you open" from drifting
    // apart. Gated by PERMISSION (requiredPermission), never role name -
    // which roles get which permission is a backend
    // system.roles_permissions assignment, not a list in this file.
    this.menuItems = [
      {
        label: AREAS.home.label,
        icon: `pi pi-fw ${AREAS.home.icon}`,
        routerLink: AREAS.home.path,
        requiredPermission: AREAS.home.requiredPermission
      },
      {
        label: AREAS.services.label,
        icon: `pi pi-fw ${AREAS.services.icon}`,
        routerLink: AREAS.services.path,
        requiredPermission: AREAS.services.requiredPermission
      },
      {
        label: 'Operations',
        icon: 'pi pi-fw pi-chart-pie',
        routerLink: AREAS.operations.path,
        requiredPermission: AREAS.operations.requiredPermission
      },
      {
        label: AREAS.support.label,
        icon: `pi pi-fw ${AREAS.support.icon}`,
        routerLink: AREAS.support.path,
        requiredPermission: AREAS.support.requiredPermission
      },
      {
        label: AREAS.communications.label,
        icon: `pi pi-fw ${AREAS.communications.icon}`,
        routerLink: AREAS.communications.path,
        requiredPermission: AREAS.communications.requiredPermission
      },
      {
        label: AREAS.projects.label,
        icon: `pi pi-fw ${AREAS.projects.icon}`,
        routerLink: AREAS.projects.path,
        requiredPermission: AREAS.projects.requiredPermission
      },
      {
        label: AREAS.portalManagement.label,
        icon: `pi pi-fw ${AREAS.portalManagement.icon}`,
        routerLink: AREAS.portalManagement.path,
        requiredPermission: AREAS.portalManagement.requiredPermission
      },
      {
        label: AREAS.systemManagement.label,
        icon: `pi pi-fw ${AREAS.systemManagement.icon}`,
        routerLink: AREAS.systemManagement.path,
        requiredPermission: AREAS.systemManagement.requiredPermission
      }
    ];

    this.userService.currentUser$.subscribe((user) => {
      if (user) {
        this.tcs.getTenantConfig(user?.tenant_id).subscribe((res) => {
          console.log('tenant config', res);
          this.tcs.storeTenantConfigToLocalStorage(res);
        });
      } else {
        this.tcs.storeTenantConfigToLocalStorage(null);
      }
    });
  }

  checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onCloseSidebar(): void {
    this.closeSidebar.emit();
  }

  convertTenantsToMenuItems(tenants: Array<{ id: number; name: string }>) {
    return tenants.map((tenant) => {
      return {
        label: tenant.name,
        icon: 'pi pi-wifi',
        command: () => {
          this.switchTenant(tenant.id);
        }
      };
    });
  }

  getCurrentTenantName(tenant_id: number, tenants: Array<{ id: number; name: string }>): string {
    const tenant = tenants.find((t) => t.id === tenant_id);
    return tenant ? tenant.name : '';
  }

  switchTenant(tenant_id: number) {
    this.userService.switchTenant(tenant_id).subscribe((res) => {
      console.log('switched tenant', res);
      this.tcs.getTenantConfig(tenant_id).subscribe((config) => {
        this.tcs.storeTenantConfigToLocalStorage(config);
      });
      window.location.reload();
    });
  }

  getVisibleMenuItems(): MenuItemWithPermission[] {
    return this.menuItems
      .filter((menuItem) => !menuItem.requiredPermission || this.authz.hasPermission(menuItem.requiredPermission))
      .slice(0, 5); // Limit to 5 items for mobile tabs
  }
}
